/* Dual-thumb range slider. Design notes live in RangeSlider.css.
   Markup contract:

     <div class="range-slider" data-unit="kr/md">
       <output aria-hidden="true"></output>
       <output aria-hidden="true"></output>
       <input type="range" min="0" max="20000" step="500" value="0" aria-label="…">
       <input type="range" min="0" max="20000" step="500" value="9000" aria-label="…">
     </div>

   The two <output> bubbles are optional; without them the track still works. */

import { formatDanishNumber } from '@/utils/number';

function wire(root: HTMLElement) {
  const inputs = Array.from(root.querySelectorAll<HTMLInputElement>('input[type="range"]'));
  if (inputs.length !== 2) return;

  const [lower, upper] = inputs;
  const outputs = Array.from(root.querySelectorAll<HTMLOutputElement>('output'));
  const unit = root.dataset.unit ?? '';
  outputs.forEach((output) => (output.dataset.unit = unit));

  /* data-labels turns the track into an ordinal scale: the inputs still carry
     positions, and every position reads out as the label at that index. Pipe
     separated, because the labels are short and can't contain one. */
  const labels = root.dataset.labels ? root.dataset.labels.split('|') : null;
  // data-plain drops the thousands separator - a year reads wrong as "1.985".
  const plain = root.dataset.plain !== undefined;
  const read = (value: number) => labels?.[value] ?? (plain ? String(value) : formatDanishNumber(value));

  const min = Number(lower.min || 0);
  const max = Number(lower.max || 100);
  const span = max - min || 1;

  /* Both inputs keep their authored min/max. A range input renders its thumb at
     (value - min) / (max - min) of its own width, so narrowing one input's
     bounds to fence the thumbs apart rescales that input alone: its thumb slides
     off the shared track, and clamping the other input's min drags its thumb
     along with it. The gap between the thumbs is kept in the values instead. */
  const sync = (moved?: HTMLInputElement) => {
    // Clamp the thumb that moved rather than pushing the other one along. The
    // thumbs may meet - from and to on one value is an exact match - and the
    // CSS sets the inputs a thumb apart, so meeting thumbs sit side by side
    // rather than one buried under the other.
    if (Number(lower.value) > Number(upper.value)) {
      if (moved === lower) lower.value = upper.value;
      else upper.value = lower.value;
    }

    const from = (Number(lower.value) - min) / span;
    const to = (Number(upper.value) - min) / span;

    root.style.setProperty('--range-from', String(from));
    root.style.setProperty('--range-to', String(to));

    if (outputs[0]) outputs[0].textContent = read(Number(lower.value));
    if (outputs[1]) outputs[1].textContent = read(Number(upper.value));

    // A screen reader would otherwise announce the position, which means nothing
    // on a ladder. Left alone on a numeric track, where the value is the value.
    if (labels) inputs.forEach((input) => input.setAttribute('aria-valuetext', read(Number(input.value))));

    /* Both inputs span the full track, so the upper one paints over the lower
       and swallows its thumb wherever the two meet. Lift the lower input in the
       top half of the track — that is the half where meeting means the user is
       reaching for it to drag it back down.

       '2', not '1': the class already puts both inputs on z-1, and equal
       z-index falls back to DOM order, which is the upper input. This is why
       the rule never did anything before.

       A mouse refines this on hover, below. This positional guess is what
       touch, keyboard and first paint get. */
    lower.style.zIndex = from > 0.5 ? '2' : '';
  };

  /* Which input is on top decides which thumb the pointer can grab, and one
     step apart in value is only a pixel or two apart on screen. Hand the stack
     to whichever thumb the cursor is nearest, before the press lands.

     `buttons` skips moves belonging to a drag already under way: re-stacking
     mid-drag would fight the thumb being held. */
  root.addEventListener('pointermove', (event) => {
    if (event.buttons !== 0) return;

    const rect = root.getBoundingClientRect();
    const at = min + ((event.clientX - rect.left) / (rect.width || 1)) * span;
    const middle = (Number(lower.value) + Number(upper.value)) / 2;

    lower.style.zIndex = at < middle ? '2' : '';
  });

  lower.addEventListener('input', () => sync(lower));
  upper.addEventListener('input', () => sync(upper));
  sync();
}

export default function initRangeSlider(scope: ParentNode = document) {
  scope.querySelectorAll<HTMLElement>('.range-slider').forEach(wire);
}
